import { useFormik } from 'formik'
import React, { useState } from 'react'
import { Modal } from 'react-bootstrap'
import * as Yup from 'yup'
import { axiosClientAuth } from '../../../app/utils/axiosClient'
import { KTIcon } from '../../helpers'
import Swal from 'sweetalert2'
import clsx from 'clsx'
import { useQueryClient } from 'react-query'
import { RESOURCE_ANALYTICS_CACHE_KEY } from '../../../app/hooks/useResourceAnalytics'
import { useIntl } from 'react-intl'

type Props = {
  show: boolean
  handleClose: () => void
}

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .max(50, 'IP Name must not exceed 50 characters')
    .matches(/^[a-zA-Z0-9 _-]+$/, 'Name must not contain special characters')
    .required('Name is required'),
  address: Yup.string()
    .matches(
      /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$|^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/,
      'Invalid IP address format'
    )
    .required('IP Address is required'),
})

const initialValues = {
  name: '',
  address: '',
}

const IPRegistrationModal = ({ show, handleClose }: Props) => {
  const intl = useIntl()
  const [loading, setLoading] = useState(false)
  const queryClient = useQueryClient()

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true)
      try {
        const res = await axiosClientAuth.post('/ip/create/', {
          title: values.name,
          address: values.address,
        })
        if (res.data.status) {
          Swal.fire({
            titleText: intl.formatMessage({ id: 'COMMON.ALERTS.SUCCESS' }),
            text: intl.formatMessage({ id: 'MYIP.MESSAGES.IP_CREATED' }),
            icon: 'success',
            confirmButtonText: intl.formatMessage({ id: 'COMMON.BUTTON.OK_GOT_IT' }),
          }).then(() => {
            queryClient.invalidateQueries('ip-list')
            queryClient.invalidateQueries(RESOURCE_ANALYTICS_CACHE_KEY)
            closeModal()
          })
        }
        setLoading(false)
      } catch (error: any) {
        const responseData = error?.response?.data
        // Pull the first message from data.address array if present, else fall back to top-level message
        const addressErrors: string[] = responseData?.data?.address || []
        const errorText =
          addressErrors.length > 0
            ? addressErrors[0]
            : responseData?.message || intl.formatMessage({ id: 'COMMON.MESSAGES.SOMETHING_WENT_WRONG' })

        Swal.fire({
          titleText: intl.formatMessage({ id: 'COMMON.ALERTS.ERROR' }),
          text: errorText,
          icon: 'error',
          confirmButtonText: intl.formatMessage({ id: 'COMMON.BUTTON.OK_GOT_IT' }),
        })
        setLoading(false)
      }
    },
  })

  const closeModal = () => {
    formik.resetForm()
    handleClose()
  }

  return (
    <Modal
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered'
      show={show}
      onHide={handleClose}
    >
      <div className='modal-body py-lg-10 px-lg-10'>
        <div className='d-flex justify-content-end'>
          <button
            className='ms-auto btn btn-sm btn-icon btn-active-color-primary'
            onClick={closeModal}
          >
            <KTIcon iconName='cross' className='text-gray fs-1' />
          </button>
        </div>

        <form className='form w-100' onSubmit={formik.handleSubmit} noValidate>
          <div className='mb-13 text-center'>
            <h1 className='mb-3'>{intl.formatMessage({ id: 'MYIP.MODAL.IP_REGISTRATION_TITLE' })}</h1>
            <div className='text-muted fw-semibold fs-5'>
              {intl.formatMessage({ id: 'MYIP.MODAL.ADD_IP_DESC' })}
            </div>
          </div>

          {formik.status ? (
            <div className='mb-lg-15 alert alert-danger'>
              <div className='alert-text font-weight-bold'>{formik.status}</div>
            </div>
          ) : null}

          <div className='fv-row mb-8'>
            <label className='form-label fs-6 fw-bolder text-dark required'>
              {intl.formatMessage({ id: 'MYIP.FORM.IP_NAME_LABEL' })}
            </label>
            <input
              placeholder={intl.formatMessage({ id: 'MYIP.FORM.IP_NAME_PLACEHOLDER' })}
              {...formik.getFieldProps('name')}
              className={clsx(
                'form-control bg-transparent',
                { 'is-invalid': formik.touched.name && formik.errors.name },
                { 'is-valid': formik.touched.name && !formik.errors.name }
              )}
              type='text'
              name='name'
              maxLength={50}
              autoComplete='off'
            />
            <div className='d-flex justify-content-between align-items-start mt-1'>
              <div>
                {formik.touched.name && formik.errors.name && (
                  <div className='fv-plugins-message-container'>
                    <span role='alert' className='text-danger'>{formik.errors.name}</span>
                  </div>
                )}
              </div>
              <span className='text-muted fs-8'>
                {formik.values.name.length}/50
              </span>
            </div>
          </div>

          <div className='fv-row mb-8'>
            <label className='form-label fs-6 fw-bolder text-dark required'>
              {intl.formatMessage({ id: 'MYIP.FORM.IP_ADDRESS_LABEL' })}
            </label>
            <input
              placeholder={intl.formatMessage({ id: 'MYIP.FORM.IP_ADDRESS_PLACEHOLDER' })}
              {...formik.getFieldProps('address')}
              className={clsx(
                'form-control bg-transparent',
                { 'is-invalid': formik.touched.address && formik.errors.address },
                {
                  'is-valid': formik.touched.address && !formik.errors.address,
                }
              )}
              type='text'
              name='address'
              autoComplete='off'
            />
            {formik.touched.address && formik.errors.address && (
              <div className='fv-plugins-message-container'>
                <span role='alert' className='text-danger'>{formik.errors.address}</span>
              </div>
            )}
          </div>

          {/* begin::Action */}
          <div className='text-center mb-10'>
            <button type='reset' className='btn btn-light me-3' onClick={closeModal}>
              <span className='indicator-label'>{intl.formatMessage({ id: 'COMMON.BUTTON.CANCEL' })}</span>
            </button>
            <button
              type='submit'
              className='btn btn-primary'
              disabled={formik.isSubmitting || !formik.isValid}
            >
              {!loading && <span className='indicator-label'>{intl.formatMessage({ id: 'COMMON.BUTTON.SUBMIT' })}</span>}
              {loading && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  {intl.formatMessage({ id: 'COMMON.MESSAGES.PLEASE_WAIT' })}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default IPRegistrationModal
